import { Column, Index, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import type { ContactClientEntity } from './contact-client.entity';

/** How a person was last contacted (the stored summary below). */
export type ContactInteractionType = 'visit' | 'call' | 'email' | 'note';

/**
 * A `person` is a human; a `department` is a shared inbox or role line on a
 * customer (ap@…, "Accounts Payable") that many records name but no single
 * person owns. Departments are never merged across customers.
 */
export type ContactKind = 'person' | 'department';

/**
 * Where a person-level value came from, so an ERP change can be followed per
 * record (plan section 1, M3): the contact record that supplied it and when.
 * `recordId` null means the value was edited in Workd.
 */
export interface ContactValueSourceModel {
	recordId: number | null;
	changedAt: string;
	/** Tenant user who edited the value in Workd (recordId null); absent for older edits. */
	userId?: number | null;
}

export type ContactValueSourcesModel = Partial<
	Record<'name' | 'email' | 'mobilePhone' | 'phone' | 'title', ContactValueSourceModel>
>;

/**
 * A person, linked to any number of customers through `contact_client`
 * (PD-17064; docs/claude/sales-relationships/01-contacts.md).
 *
 * The per-customer contact records in `client_contact` stay exactly as the
 * ERP, SM1 and imports write them; `contact_record` maps each of those to its
 * person. Nothing here is written onto `client_contact` by matching: touching
 * that row bumps `_modified_date`, which the NetSuite contact export reads as
 * "edited in Workd" and would send every contact back to the ERP.
 *
 * The activity columns are stored summaries so the Contacts list (hundreds of
 * thousands of rows on large tenants) reads every column from this row or one
 * indexed join, never from activity tables at query time. They are kept
 * current on write, by the contact sweep, and by a nightly reconcile.
 */
@Index('contact__name', ['name'], {})
@Index('contact__match_key', ['matchKey'], {})
@Index('contact__email_key', ['emailKey'], {})
@Index('contact__phone_key', ['phoneKey'], {})
@Index('contact__fk_client_primary', ['fkClientIdPrimary'], {})
@Index('contact__last_contacted', ['lastContactedDate'], {})
@Entity('contact')
export class ContactEntity extends BaseEntity {
	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	@Column('varchar', { name: 'kind', length: 32, default: () => "'person'" })
	public kind!: ContactKind;

	/** The person's own email (never a shared inbox). */
	@Column('varchar', { name: 'email', length: 255, nullable: true })
	public email!: string | null;

	@Column('varchar', { name: 'mobilePhone', length: 64, nullable: true })
	public mobilePhone!: string | null;

	/** Main office phone, from the records when no per-customer value exists. */
	@Column('varchar', { name: 'phone', length: 64, nullable: true })
	public phone!: string | null;

	/** Title at the main customer, shown under the name in lists. */
	@Column('varchar', { name: 'title', length: 255, nullable: true })
	public title!: string | null;

	@Column('text', { name: 'note', nullable: true })
	public note!: string | null;

	@Column('tinyint', { name: 'isDoNotEmail', width: 1, default: () => "'0'" })
	public isDoNotEmail!: boolean;

	@Column('tinyint', { name: 'isDoNotCall', width: 1, default: () => "'0'" })
	public isDoNotCall!: boolean;

	/** True while at least one customer link is active. */
	@Column('tinyint', { name: 'isActive', width: 1, default: () => "'1'" })
	public isActive!: boolean;

	/** Normalized full name, the matching key (see contact-normalization.util). */
	@Column('varchar', { name: 'matchKey', length: 255, nullable: true })
	public matchKey!: string | null;

	/** Lower-cased personal email, the matching key for email. */
	@Column('varchar', { name: 'emailKey', length: 255, nullable: true })
	public emailKey!: string | null;

	/** Last ten digits of the mobile (else office) phone, for matching. */
	@Column('varchar', { name: 'phoneKey', length: 16, nullable: true })
	public phoneKey!: string | null;

	@Column('json', { name: 'valueSources', nullable: true })
	public valueSources!: ContactValueSourcesModel | null;

	// --- Stored summaries (kept current on write, by the sweep, and nightly) ---

	@Column('int', { name: 'fk_client__id_primary', unsigned: true, nullable: true })
	public fkClientIdPrimary!: number | null;

	@Column('int', { name: 'customerCount', unsigned: true, default: () => "'0'" })
	public customerCount!: number;

	/** Role names across active links, comma-separated, for badges and filters. */
	@Column('varchar', { name: 'roleSummary', length: 500, nullable: true })
	public roleSummary!: string | null;

	@Column('datetime', { name: 'lastContactedDate', nullable: true })
	public lastContactedDate!: Date | null;

	@Column('varchar', { name: 'lastContactedType', length: 32, nullable: true })
	public lastContactedType!: ContactInteractionType | null;

	@Column('int', { name: 'fk_user__id_lastContacted', unsigned: true, nullable: true })
	public fkUserIdLastContacted!: number | null;

	@Column('datetime', { name: 'lastVisitDate', nullable: true })
	public lastVisitDate!: Date | null;

	@Column('datetime', { name: 'lastCallDate', nullable: true })
	public lastCallDate!: Date | null;

	@Column('datetime', { name: 'lastEmailDate', nullable: true })
	public lastEmailDate!: Date | null;

	@Column('datetime', { name: 'nextTaskDueDate', nullable: true })
	public nextTaskDueDate!: Date | null;

	@ManyToOne(() => ClientEntity, { onDelete: 'SET NULL', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_client__id_primary', referencedColumnName: 'id' }])
	public primaryClient!: ClientEntity | null;

	@OneToMany('ContactClientEntity', 'contact')
	public contactClients!: Array<ContactClientEntity>;
}
